import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createClient } from '@app/shared/utils/query_client';
import { RouterApp } from '@app/shared/components/Router';
import { createIntlProvider, getMessages } from '@app/shared/contexts/IntlProvider';
import { SentryProvider } from '@app/shared/contexts/SentryProvider';
import { FenixProvider } from '@app/shared/contexts/FenixProvider';
import { usePublicEnv, PublicEnvProvider, getPublicEnv } from '@app/shared/contexts/PublicEnv';
import { QueryClientProvider } from 'react-query';
import { TrackerProvider } from '@app/shared/contexts/TrackerProvider';
import { LinkProvider } from '@app/shared/contexts/LinkProvider';
import { AuthProvider } from '@app/shared/contexts/AuthProvider';
import { AndroidEventProvider } from '@app/shared/contexts/AndroidEventProvider';
import { getInitialData } from '@app/shared/utils/initialData';
import { GlobalError } from '@app/shared/components/GlobalError';

const IntlProviderWrapper = ({ children, initialMessages }) => {
  const env = usePublicEnv();
  const locale = env?.locale;
  const InnerProvider = React.useMemo(() => createIntlProvider(locale, getMessages), [locale]);
  return <InnerProvider initialMessages={initialMessages}>{children}</InnerProvider>;
};

const ssrProps = getInitialData();

const Root = ({
  context = ssrProps?.context,
  messages = ssrProps?.messages,
  tracker = ssrProps?.tracker,
  trackerConfig = ssrProps?.trackerConfig,
}) => {
  const [queryClient] = useState(() => createClient());
  return (
    <GlobalError fenixVersion={context?.fenixVersion}>
      <PublicEnvProvider context={context} getPublicEnv={getPublicEnv}>
        <AuthProvider>
          <SentryProvider>
            <FenixProvider config={{ fenixVersion: context?.fenixVersion }}>
              <IntlProviderWrapper initialMessages={messages}>
                <AndroidEventProvider>
                  <TrackerProvider tracker={tracker} config={trackerConfig}>
                    <LinkProvider>
                      <QueryClientProvider client={queryClient}>
                        <RouterApp />
                      </QueryClientProvider>
                    </LinkProvider>
                  </TrackerProvider>
                </AndroidEventProvider>
              </IntlProviderWrapper>
            </FenixProvider>
          </SentryProvider>
        </AuthProvider>
      </PublicEnvProvider>
    </GlobalError>
  );
};

Root.defaultProps = { ...ssrProps };

Root.propTypes = {
  context: PropTypes.object,
  messages: PropTypes.shape({}),
  name: PropTypes.string,
  tracker: PropTypes.object,
  trackerConfig: PropTypes.object,
  fwfConfig: PropTypes.object,
  sentryConfig: PropTypes.object,
  isServer: PropTypes.bool,
};

export default Root;
